import React, { type FunctionComponent, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { css } from '@emotion/react'
import { InformationIcon } from '@instacart/ids-core'
import { colors, Tooltip } from '@retailer-platform/shared-components'
import { type AllocationType } from 'domains/shifts/components/types'
import { StaffingLevelCell } from 'domains/shifts/components/components/staffing-levels/StaffingLevelCell'
import { fontSize } from 'gin-and-tonic/foundation/fontSize'
import { fontWeight } from 'gin-and-tonic/foundation/fontWeight'
import { spacing } from 'gin-and-tonic/foundation/spacing'
import { getStaffingLevelsForShiftType } from 'domains/shifts/components/components/staffing-levels/utils/getStaffingLevelsForShiftType'
import {
  type StaffingLevel,
  type StaffingLevelData,
} from 'domains/shifts/components/components/staffing-levels/utils/types'
import { useShiftsStateContext } from 'domains/shifts/components/contexts/ShiftsStateContext'

const containerStyles = css({
  display: 'flex',
  height: '32px',
  borderBottom: `1px solid ${colors.GRAYSCALE.X0}`,
})

const headerStyles = css({
  display: 'flex',
  alignItems: 'center',
  color: colors.GRAYSCALE.X50,
  textTransform: 'uppercase',
  fontSize: fontSize.X11,
  fontWeight: fontWeight.SEMIBOLD,
  width: 260,
  paddingLeft: spacing.X32,
  background: colors.GRAYSCALE.X0,
})

const tooltipIconStyles = css({
  fill: colors.GRAYSCALE.X30,
  marginLeft: spacing.X4,
})

interface StaffingLevelRowProps {
  staffingLevels: StaffingLevel[]
  allocationType?: AllocationType
  labelId: string
  tooltipLabelId?: string
}

export const StaffingLevelsRow: FunctionComponent<StaffingLevelRowProps> = ({
  staffingLevels,
  allocationType,
  labelId,
  tooltipLabelId,
}) => {
  const {
    shiftsState: { shiftType },
  } = useShiftsStateContext()
  const levels: StaffingLevelData[] = useMemo(
    () => getStaffingLevelsForShiftType(staffingLevels, shiftType, allocationType),
    [staffingLevels, shiftType, allocationType]
  )

  return (
    <div css={containerStyles}>
      <div css={headerStyles}>
        <FormattedMessage id={labelId} />

        {tooltipLabelId && (
          <Tooltip
            placement="bottom"
            target={<InformationIcon size={16} css={tooltipIconStyles} />}
          >
            <FormattedMessage id={tooltipLabelId} />
          </Tooltip>
        )}
      </div>

      {levels.map(({ scheduled, required, closed }, i) => (
        <StaffingLevelCell
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          scheduled={scheduled}
          required={required}
          closed={closed}
        />
      ))}
    </div>
  )
}
